import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, FileBarChart, Loader2, Mail, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AppHeader } from "@/components/AppHeader";
import { KpiStrip } from "@/components/KpiStrip";
import { Headlines, StatusCards } from "@/components/StatusCards";
import { adminApi, errMsg } from "@/lib/api";
import { computeSynopsis } from "@/lib/calc";
import { fmtDate, StatusBadge } from "@/pages/AdminDashboard";

const Notes = ({ cid }) => {
  const [list, setList] = useState([]);
  const [text, setText] = useState("");
  const load = () => adminApi.notes(cid).then(setList);
  useEffect(() => { load(); }, [cid]); // eslint-disable-line react-hooks/exhaustive-deps
  const add = async () => { if (!text.trim()) return; await adminApi.addNote(cid, text.trim()); setText(""); load(); };
  return (
    <section className="panel" data-testid="notes-panel">
      <div className="panel-head"><h3>Private advisor notes</h3><span className="text-[11px] text-slate-400">never visible to the client</span></div>
      <Textarea data-testid="note-input" rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a private note…" className="bg-white" />
      <Button size="sm" className="mt-2" onClick={add} data-testid="note-add-button">Add note</Button>
      <div className="mt-3 space-y-2">
        {list.map((n) => (
          <div key={n.id} className="rounded border border-slate-200 bg-slate-50 p-2 text-sm" data-testid={`note-${n.id}`}>
            <div className="flex justify-between text-[11px] text-slate-400"><span>{fmtDate(n.created_at)}</span>
              <button type="button" onClick={async () => { await adminApi.deleteNote(n.id); load(); }} data-testid={`note-delete-${n.id}`} aria-label="Delete note"><Trash2 className="h-3.5 w-3.5 text-rose-500" /></button></div>
            <p className="whitespace-pre-wrap text-slate-800">{n.text}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

const ShareButton = ({ sid, sharedAt, onDone }) => {
  const [busy, setBusy] = useState(false);
  const share = async () => {
    setBusy(true);
    try {
      const r = await adminApi.shareReport(sid);
      if (r.email_sent) toast.success("Report shared. The client was emailed a secure sign-in link.");
      else toast.warning("Report shared in the client's portal, but the email could not be sent right now. Try Re-send in a few minutes.");
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return <Button size="sm" onClick={share} disabled={busy} data-testid="submission-share-button" className="gap-1.5">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} {sharedAt ? "Re-send report to client" : "Send report to client"}</Button>;
};

export default function SubmissionPage() {
  const { sid } = useParams();
  const nav = useNavigate();
  const [x, setX] = useState(null);
  const load = () => adminApi.submission(sid).then(setX).catch((e) => { toast.error(errMsg(e)); nav("/admin"); });
  useEffect(() => { load(); }, [sid]); // eslint-disable-line react-hooks/exhaustive-deps
  const s = useMemo(() => (x ? computeSynopsis(x.data) : null), [x]);
  if (!s) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[#0072C6]" /></div>;
  const cur = s.d.profile.currency;
  return (
    <div className="min-h-screen">
      <AppHeader>
        <Button variant="ghost" size="sm" onClick={() => nav("/admin")} data-testid="submission-back-button" className="gap-1.5"><ArrowLeft className="h-4 w-4" /> Dashboard</Button>
      </AppHeader>
      <main className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-6 space-y-4" data-testid="submission-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="metric-label">Submitted financial assessment</p>
            <h1 className="font-heading text-2xl font-extrabold text-[#0b2545]" data-testid="submission-client-name">{x.name}</h1>
            <p className="text-sm text-slate-500">{x.email} · version {x.version} · submitted {fmtDate(x.submitted_at)} · <StatusBadge status={x.status} /></p>
            <p className="text-[11px] text-slate-400" data-testid="submission-consent">Consent: privacy notice {x.consent?.privacy_version} accepted {fmtDate(x.consent?.at)}</p>
            {x.shared_at && <p className="text-[11px] font-semibold text-emerald-700" data-testid="submission-shared-at">Report sent to client {fmtDate(x.shared_at)}</p>}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => nav(`/admin/submissions/${sid}/report`)} data-testid="submission-report-button" className="gap-1.5"><FileBarChart className="h-4 w-4" /> PDF report</Button>
            <ShareButton sid={sid} sharedAt={x.shared_at} onDone={load} />
            <Button variant="outline" size="sm" onClick={() => nav(`/clients/${x.client_id}`)} data-testid="submission-open-editor-button" className="gap-1.5"><Pencil className="h-4 w-4" /> Open in full editor</Button>
            {x.status !== "reviewed" && <Button size="sm" onClick={async () => { await adminApi.review(sid); toast.success("Marked as reviewed"); load(); }} data-testid="submission-review-button" className="gap-1.5"><CheckCircle2 className="h-4 w-4" /> Mark review completed</Button>}
          </div>
        </div>
        {x.versions.length > 1 && (
          <div className="flex flex-wrap gap-1.5 text-xs" data-testid="submission-versions">
            {x.versions.map((v) => <button key={v.id} type="button" onClick={() => nav(`/admin/submissions/${v.id}`)} className={`version-chip ${v.id === sid ? "is-active" : ""}`} data-testid={`version-${v.version}`}>v{v.version} · {fmtDate(v.submitted_at)}</button>)}
          </div>
        )}
        <KpiStrip s={s} cur={cur} />
        <div className="grid lg:grid-cols-[1fr_340px] gap-4 items-start">
          <div className="space-y-4"><Headlines s={s} cur={cur} testId="submission-headlines" /><StatusCards s={s} cur={cur} testId="submission-status" /></div>
          <Notes cid={x.client_id} />
        </div>
        <p className="text-[11px] text-slate-400">This is the permanent snapshot the client submitted. "Open in full editor" shows the client's current record with all tabs, calculations, Meeting Mode and reports.</p>
      </main>
    </div>
  );
}
